import { Component, OnInit, Input, ElementRef, Injectable, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Marked } from 'marked';
import { markedHighlight } from 'marked-highlight';
import hljs from 'highlight.js';
import { ListServicesService } from '../../services/list-services.service';
import { first, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { isEmpty } from 'lodash-es';


@Injectable()
class EvaServiceHighLighter implements OnDestroy {

  codeElementsMap = new Map<HTMLElement, EventListenerOrEventListenerObject>();

  constructor(protected $listServices: ListServicesService, protected router: Router) { }

  /**
   * The docs tend to refer to other services in <code> </code> blocks
   * We will query all elements in the generated markdown, checkout all the code nodes and see if they are referring to any services
   */
  async highlightPotentialServices(el: HTMLElement) {

    const serviceNames = await this.$listServices.services$.pipe(
      map( values => values.map( service => service.Name ) ),
      first( values => !isEmpty(values))
    ).toPromise();

    Array.from(el.querySelectorAll('code')).filter( codeElement => {
      return serviceNames.includes(codeElement.innerText);
    })
    .forEach( codeElement => {

      codeElement.style.cursor = 'pointer';

      const handler = () => {
        this.router.navigate(['/service/' + codeElement.innerText]);
      };

      codeElement.addEventListener('click', handler);

      this.codeElementsMap.set(codeElement, handler);
    });
  }

  async getPotentialServices(el: HTMLElement) {
    const serviceNames = await this.$listServices.services$.pipe(
      map(values => values.map(service => service.Name)),
      first(values => !isEmpty(values))
    ).toPromise();

    const potentialService = Array.from(el.querySelectorAll('code')).filter(codeElement => {
      return serviceNames.map( serviceName => serviceName.toLowerCase()).includes(codeElement.innerText.toLowerCase());
    }).map(codeElement => codeElement.innerText);

    return potentialService;
  }


  ngOnDestroy() {
    Array.from(this.codeElementsMap).forEach( ([codeElement, listener]) => {
      codeElement.removeEventListener('click', listener);
    });
  }
}

@Component({
    selector: 'eva-markdown-html',
    templateUrl: './markdown-html.component.html',
    styleUrls: ['./markdown-html.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MarkdownHtmlComponent extends EvaServiceHighLighter implements OnInit  {

  private converter = new Marked(
    markedHighlight({
      langPrefix: 'hljs language-',
      highlight(code, lang) {
        const language = hljs.getLanguage(lang) ? lang : 'plaintext';
        return hljs.highlight(code, { language }).value;
      },
    }),
  );

  private _markdown: string;

  public get markdown(): string {
    return this._markdown;
  }

  @Input()
  public set markdown(markdown: string) {
    this.convert(markdown);

    this._markdown = markdown;
  }

  @Output() potentialServicesCallback = new EventEmitter();

  constructor(private el: ElementRef, protected $listServices: ListServicesService, protected router: Router) {
    super($listServices, router);
  }

  ngOnInit() {

  }

  async convert(markdown: string) {
    const html = this.converter.parse(markdown ?? '', { async: false }) as string;

    (this.el.nativeElement as HTMLElement).innerHTML = html;

    this.highlightPotentialServices(this.el.nativeElement);

    const potentialServices = await this.getPotentialServices(this.el.nativeElement);

    this.potentialServicesCallback.emit(potentialServices);
  }
}
